import { acceptHMRUpdate, defineStore, storeToRefs } from 'pinia';
import type { ProfileConfig } from '@/types/edit/ProfileConfig.ts';
import type { PromptConfig } from '@/types/edit/PromptConfig.ts';
import type { VariableConfig } from '@/types/edit/VariableConfig.ts';
import type { StepConfig } from '@/types/edit/StepConfig.ts';
import { watch } from 'vue';
import { syncToWebViewAction } from '@/actions/sync/SyncToWebViewAction.ts';
import { SYNC_TO_WEB_VIEW_KEYS } from '@/types/sync/SyncKeys.ts';

interface State {
    profileConfigsString: string;
    stepConfigsString: string;
    promptConfigsString: string;
    variableConfigsString: string;
}

export const usePromptStore = defineStore('v3/prompt', {
    state: (): State => {
        return {
            profileConfigsString: '{}',
            stepConfigsString: '{}',
            promptConfigsString: '{}',
            variableConfigsString: '{}',
        };
    },
    getters: {
        profileConfigs(state): Record<ProfileConfig['id'], ProfileConfig> {
            return JSON.parse(state.profileConfigsString);
        },
        stepConfigs(state): Record<StepConfig['id'], StepConfig> {
            return JSON.parse(state.stepConfigsString);
        },
        promptConfigs(state): Record<PromptConfig['id'], PromptConfig> {
            return JSON.parse(state.promptConfigsString);
        },
        variableConfigs(state): Record<VariableConfig['id'], VariableConfig> {
            return JSON.parse(state.variableConfigsString);
        },
    },
    actions: {
        setProfileConfigs(data: Record<ProfileConfig['id'], ProfileConfig>) {
            this.profileConfigsString = JSON.stringify(data);
        },
        setStepConfigs(data: Record<StepConfig['id'], StepConfig>) {
            this.stepConfigsString = JSON.stringify(data);
        },
        setPromptConfigs(data: Record<PromptConfig['id'], PromptConfig>) {
            this.promptConfigsString = JSON.stringify(data);
        },
        setVariableConfigs(data: Record<VariableConfig['id'], VariableConfig>) {
            this.variableConfigsString = JSON.stringify(data);
        },
        replaceProfileGraph(
            profileId: ProfileConfig['id'],
            profileConfig: ProfileConfig,
            stepConfigs: Record<StepConfig['id'], StepConfig>,
            promptConfigs: Record<PromptConfig['id'], PromptConfig>,
        ) {
            const nextProfiles = Object.fromEntries(
                Object.entries(this.profileConfigs).filter(([id]) => id !== profileId),
            );
            const nextSteps = Object.fromEntries(
                Object.entries(this.stepConfigs).filter(
                    ([, config]) => config.profileId !== profileId,
                ),
            );
            const nextPrompts = Object.fromEntries(
                Object.entries(this.promptConfigs).filter(
                    ([, config]) => config.profileId !== profileId,
                ),
            );

            if (profileConfig.editable) {
                nextProfiles[profileId] = profileConfig;
                Object.assign(
                    nextSteps,
                    Object.fromEntries(
                        Object.entries(stepConfigs).filter(
                            ([, config]) => config.profileId === profileId && config.editable,
                        ),
                    ),
                );
                Object.assign(
                    nextPrompts,
                    Object.fromEntries(
                        Object.entries(promptConfigs).filter(
                            ([, config]) => config.profileId === profileId && config.editable,
                        ),
                    ),
                );
            }

            this.$patch({
                profileConfigsString: JSON.stringify(nextProfiles),
                stepConfigsString: JSON.stringify(nextSteps),
                promptConfigsString: JSON.stringify(nextPrompts),
            });
        },
    },
    persist: {
        storage: localStorage,
    },
});

/**
 * @see storeSyncPromptHandler
 * @see syncStoresToWebViewAction
 */
export const promptStoreWatcherHandler = (
    profileConfigsString: string,
    stepConfigsString: string,
    promptConfigsString: string,
    variableConfigsString: string,
) => {
    syncToWebViewAction(
        SYNC_TO_WEB_VIEW_KEYS.storeSyncPrompt,
        JSON.stringify({
            profileConfigsString: profileConfigsString,
            stepConfigsString: stepConfigsString,
            promptConfigsString: promptConfigsString,
            variableConfigsString: variableConfigsString,
        }),
    );
};

export const registerPromptStoreWatcher = () => {
    const promptStore = usePromptStore();
    const { profileConfigsString, stepConfigsString, promptConfigsString, variableConfigsString } =
        storeToRefs(promptStore);

    watch(
        [profileConfigsString, stepConfigsString, promptConfigsString, variableConfigsString],
        async ([
            newProfileConfigsString,
            newStepConfigsString,
            newPromptConfigsString,
            newVariableConfigsString,
        ]) => {
            promptStoreWatcherHandler(
                newProfileConfigsString,
                newStepConfigsString,
                newPromptConfigsString,
                newVariableConfigsString,
            );
        },
        { deep: true },
    );
};

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(usePromptStore, import.meta.hot));
}
